"use client"

import type React from "react"
import { Modal, ModalHeader, ModalBody, Row, Col, Badge, Table } from "reactstrap"
import type { VehicleVisit } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  visitData: VehicleVisit | null
}

const VehicleVisitModal: React.FC<Props> = ({ isOpen, toggle, visitData }) => {
  if (!visitData) return null

  const getStatusBadge = (status: string) => {
    const statusColors = {
      entered: "warning",
      exited: "success",
      cancelled: "danger",
    }
    return (
      <Badge color={statusColors[status as keyof typeof statusColors] || "secondary"} pill>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Vehicle Visit Details</ModalHeader>
      <ModalBody>
        <Row>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Plate Number</h6>
              <p className="mb-0">
                <strong>{visitData.plate}</strong>
              </p>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Status</h6>
              <p className="mb-0">{getStatusBadge(visitData.status)}</p>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Gate</h6>
              <p className="mb-0">
                <Badge color="info" pill>
                  {visitData.gate_assignment_info?.gate.replace("_", " ").toUpperCase() || "N/A"}
                </Badge>
              </p>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Gate Operator</h6>
              <p className="mb-0">{visitData.gate_assignment_info?.user_name || "N/A"}</p>
            </div>
          </Col>
        </Row>

        <hr />

        <Row>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Entry Time</h6>
              <p className="mb-0">
                {visitData.entered_at ? (
                  <>
                    <div>{new Date(visitData.entered_at).toLocaleDateString()}</div>
                    <small className="text-muted">{new Date(visitData.entered_at).toLocaleTimeString()}</small>
                  </>
                ) : (
                  <span className="text-muted">Not recorded</span>
                )}
              </p>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Exit Time</h6>
              <p className="mb-0">
                {visitData.exited_at ? (
                  <>
                    <div>{new Date(visitData.exited_at).toLocaleDateString()}</div>
                    <small className="text-muted">{new Date(visitData.exited_at).toLocaleTimeString()}</small>
                  </>
                ) : (
                  <span className="text-muted">Still inside</span>
                )}
              </p>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Duration</h6>
              <p className="mb-0">
                {visitData.duration_minutes ? (
                  <span>{visitData.duration_minutes} minutes</span>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </p>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Parking Fee</h6>
              <p className="mb-0">
                {visitData.amount ? (
                  <strong>FRW {visitData.amount.toLocaleString()}</strong>
                ) : (
                  <span className="text-muted">Not calculated</span>
                )}
              </p>
            </div>
          </Col>
        </Row>

        {visitData.tx_reference && (
          <>
            <hr />
            <h6>Payment Information</h6>
            <Table size="sm" borderless>
              <tbody>
                <tr>
                  <td>
                    <strong>Transaction Reference:</strong>
                  </td>
                  <td>{visitData.tx_reference}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Operator:</strong>
                  </td>
                  <td>
                    <Badge color="info">{visitData.operator}</Badge>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Phone Number:</strong>
                  </td>
                  <td>{visitData.phone_number}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Amount Paid:</strong>
                  </td>
                  <td>
                    <strong>FRW {visitData.amount?.toLocaleString()}</strong>
                  </td>
                </tr>
              </tbody>
            </Table>
          </>
        )}

        <hr />

        <Row>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Created By</h6>
              <p className="mb-0">{visitData.created_by_name || "System"}</p>
            </div>
          </Col>
          <Col md="6">
            <div className="mb-3">
              <h6 className="text-muted mb-1">Last Updated By</h6>
              <p className="mb-0">{visitData.updated_by_name || "System"}</p>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  )
}

export default VehicleVisitModal
